// utils/qzHelpers.js
// QZ Tray helpers avec cache de config par protocole (DPL/ZPL/RAW) + préférences de files.

// Marqueur de build : confirme en console que le bundle QZ est bien déployé/chargé.
console.log('[QZ] qzHelpers loaded — build 2026-05-21');

var _secInit = false;
var _connectPromise = null;

// Cache de configs : { "DPL" | "ZPL" | "RAW" : { cfg, name, proto } }
var _cfgCache = Object.create(null);

// Préférences de files par protocole (optionnel)
var _preferredQueues = Object.create(null);

function _log(){ var a=[].slice.call(arguments); a.unshift('[QZ]'); console.log.apply(console,a); }
function _err(){ var a=[].slice.call(arguments); a.unshift('[QZ]'); console.error.apply(console,a); }

function _wsActive(){
  return !!(window.qz && qz.websocket && qz.websocket.isActive());
}

// ------------------ Sécurité ------------------
/**
 * options = { algo?: 'SHA256', certUrl?: string, signUrl?: string }
 * Fournit des resolvers (resolve, reject) attendus par QZ.
 */
export function initSecurity(opts){
  if (_secInit) return;
  _secInit = true;

  var algo    = opts && opts.algo    || 'SHA256';
  var certUrl = opts && opts.certUrl || null;
  var signUrl = opts && opts.signUrl || null;

  try { qz.security.setSignatureAlgorithm(algo); } catch(e){}

  if (certUrl){
    qz.security.setCertificatePromise(function(resolve, reject){
      fetch(certUrl, { cache: 'reload' })
        .then(function(r){ if(!r.ok) throw new Error('Cert HTTP '+r.status); return r.text(); })
        .then(resolve)
        .catch(reject);
    });
  }

  if (signUrl){
    qz.security.setSignaturePromise(function(toSign){
      return function(resolve, reject){
        fetch(signUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          cache: 'no-store',
          body: JSON.stringify({ message: toSign })
        })
          .then(function(r){ if(!r.ok) throw new Error('Sign HTTP '+r.status); return r.json(); })
          .then(function(j){ j && j.signature ? resolve(j.signature) : reject(new Error('Signature manquante')); })
          .catch(reject);
      };
    });
  }
}

// ------------------ Connexion ------------------
export function ensureConnected(){
  if (_wsActive()) return Promise.resolve();
  if (_connectPromise) return _connectPromise;

  _connectPromise = qz.websocket.connect()
    .then(function(){
      _log('connecté');
      try {
        if (qz.websocket && qz.websocket.connection){
          qz.websocket.connection.onclose = function(){
            _log('websocket fermée → reset promise & cache');
            _connectPromise = null;
            _cfgCache = Object.create(null); // invalide les configs
          };
        }
      } catch(e){}
    })
    .catch(function(e){ _connectPromise = null; throw e; });

  return _connectPromise;
}

export function healthCheck(){
  if (!_wsActive()) return Promise.resolve(false);
  return qz.printers.find().then(function(){ return true; }, function(){ return false; });
}

export function reconnectSilent(maxTries, baseDelayMs){
  var tries = (typeof maxTries === 'number') ? maxTries : 6;
  var base  = (typeof baseDelayMs === 'number') ? baseDelayMs : 500;

  function loop(i){
    if (_wsActive()) return Promise.resolve();
    if (i >= tries) return Promise.reject(new Error('QZ reconnect failed'));

    var delay = Math.min(base * Math.pow(2, i), 5000);
    return new Promise(function(res){ setTimeout(res, delay); })
      .then(function(){ return ensureConnected(); })
      .catch(function(){ return loop(i+1); });
  }
  return loop(0);
}

export function ensureConnectedWithHealth(){
  return ensureConnected()
    .then(function(){ return healthCheck(); })
    .then(function(ok){
      if (ok) return;
      return reconnectSilent().then(function(){ return healthCheck(); })
        .then(function(ok2){ if (!ok2) throw new Error('QZ indisponible'); });
    });
}

// ------------------ Préférences de files (optionnel) ------------------
/** setPreferredQueues({ DPL: 'NomImprimanteDatamax', ZPL: 'NomImprimanteZebra', RAW: 'NomImprimantePDF' }) */
export function setPreferredQueues(map){
  _preferredQueues = Object.create(null);
  if (map && typeof map === 'object'){
    ['DPL','ZPL','RAW'].forEach(function(k){
      if (map[k]) _preferredQueues[k] = String(map[k]);
    });
  }
  _log('preferredQueues =', JSON.stringify(_preferredQueues));
}

// ------------------ Choix imprimante ------------------
function _cacheKeyLS(proto){
  var p=(proto||'').toUpperCase();
  return p ? ('qz_printer_name_'+p) : 'qz_printer_name';
}

// proto = 'ZPL' | 'DPL' | 'MUNBYN' | 'RAW' | null
function _pickPrinterName(proto, forceName) {
  // Normalisation du protocole
  var p = (proto || '').toUpperCase().trim();

  // 🔁 Mapping : RAW utilise la whitelist MUNBYN
  if (p === 'RAW') {
    p = 'MUNBYN';
  }

  var cacheKey = _cacheKeyLS(p);

  // Listes blanches STRICTES selon protocole
  const whitelist = {
    "ZPL":    ["ZEBRA", "LESTUDIO", "COLISSIMO"],
    "DPL":    ["DATAMAX", "LESTUDIO", "COLISSIMO"],
    "MUNBYN": ["MUNBYN", "LESTUDIO", "COLISSIMO"]
  };

  return qz.printers.find().then(function(list) {
    _log("[QZ] list =", JSON.stringify(list || []));
    if (!list || !list.length) {
      throw new Error('Aucune imprimante trouvée');
    }

    // 0) FORCE NAME
    if (forceName) {
      const fUpper = forceName.toUpperCase();
      const forced = list.find(n => n.toUpperCase() === fUpper);
      if (forced) {
        _log("[QZ] printer (forced) =", forced);
        return forced;
      }
      throw new Error("Imprimante demandée introuvable : " + forceName);
    }

    // 1) Préférences utilisateur via setPreferredQueues
    const pref = _preferredQueues && _preferredQueues[p];
    if (pref) {
      const pUpper = pref.toUpperCase();
      const prefMatch = list.find(n => n.toUpperCase() === pUpper);
      if (prefMatch) {
        try { localStorage.setItem(cacheKey, prefMatch); } catch {}
        _log("[QZ] printer (preferred) =", prefMatch);
        return prefMatch;
      }
    }

    // 2) Logique stricte par protocole (avec match partiel)
    if (whitelist[p]) {
      const allowed = whitelist[p];

      const chosen = list.find(n => {
        const upper = n.toUpperCase();
        // match si le nom contient au moins une des chaînes whitelistées
        return allowed.some(a => upper.includes(a));
      });

      if (chosen) {
        try { localStorage.setItem(cacheKey, chosen); } catch {}
        _log(`[QZ] printer (${p} strict match) =`, chosen);
        return chosen;
      }

      throw new Error(
        `Aucune imprimante compatible ${p} trouvée. Autorisées : ${allowed.join(", ")}`
      );
    }

    // 3) Sauvegarde locale (cache)
    let saved = null;
    try { saved = localStorage.getItem(cacheKey); } catch {}
    if (saved) {
      const sUpper = saved.toUpperCase();
      const savedMatch = list.find(n => n.toUpperCase() === sUpper);
      if (savedMatch) {
        _log(`[QZ] printer (saved ${p}) =`, savedMatch);
        return savedMatch;
      }
    }

    // 4) Imprimante par défaut système
    return qz.printers.getDefault()
      .catch(() => null)
      .then(function(def) {
        if (def && list.includes(def)) {
          try { localStorage.setItem(cacheKey, def); } catch {}
          _log("[QZ] printer (default) =", def);
          return def;
        }

        // 5) Fallback → première imprimante de la liste
        const fallback = list[0];

        // On ne met en cache le fallback que si c'est la seule imprimante
        if (list.length === 1) {
          try { localStorage.setItem(cacheKey, fallback); } catch {}
        }

        _log(`[QZ] printer (fallback ${p}) =`, fallback);
        return fallback;
      });
  });
}

/**
 * Récupère une config QZ en **réutilisant** le cache si possible.
 * @param {('DPL'|'ZPL'|'RAW'|null)} proto
 * @param {{ printerName?: string, jobName?: string }} options
 * @returns Promise<qz.Config>
 */
export function getThermalPrinterConfigFor(proto, options){
  var p = (proto||'RAW').toUpperCase();
  var desiredName = options && options.printerName;
  var jobName = (options && options.jobName) || ('Colissimo '+p);
  var protocoleOriginal = (options && options.protocoleOriginal || '').toUpperCase();

  // Réutiliser le cache si possible
  var cached = _cfgCache[p];
  if (_wsActive() && cached && (!desiredName || desiredName === cached.name)){
    try {
      cached.cfg.setPrintOptions && cached.cfg.setPrintOptions({ jobName: jobName, forceRaw: true });
    } catch(e){}
    _log('config reused for', p, '→', cached.name);
    return Promise.resolve(cached.cfg);
  }

  // Choisir/valider le nom d'imprimante
  return _pickPrinterName(p, desiredName).then(function(selectedName){
    if (_wsActive() && cached && cached.name === selectedName){
      _log('config reused (same printer) for', p, '→', selectedName);
      return cached.cfg;
    }

    // Créer une nouvelle config
    var isMunbyn = (protocoleOriginal === 'MUNBYN');

    // Pour MUNBYN, définir la taille du papier (10x15cm = 4x6 pouces)
    var cfgOptions = { jobName: jobName };
    if (isMunbyn) {
      cfgOptions.size = { width: 4, height: 6 };
      cfgOptions.units = 'in';
      cfgOptions.rasterize = false;
      cfgOptions.altPrinting = true;
    }

    var cfg = qz.configs.create(selectedName, cfgOptions);
    try {
      if (!isMunbyn) {
        cfg.setRasterize && cfg.setRasterize(false);
        if (p !== 'RAW') {
          cfg.setAltPrinting && cfg.setAltPrinting(true);
        }
        cfg.setPrintOptions && cfg.setPrintOptions({ forceRaw: true });
      }
      _log('config created (rasterize=false, forceRaw=' + !isMunbyn + ', altPrinting=' + (p !== 'RAW' || isMunbyn) + ', size=' + (isMunbyn ? '4x6in' : 'auto') + ').');
    } catch(e){
      _err('config flags error', e);
    }
    _cfgCache[p] = { cfg: cfg, name: selectedName, proto: p };
    return cfg;
  });
}

// Outils
export function clearPrinterConfigCache(){ _cfgCache = Object.create(null); }
export function getCachedPrinterName(proto){
  var p=(proto||'RAW').toUpperCase();
  return _cfgCache[p] && _cfgCache[p].name || null;
}

// --- Détection / initialisation QZ avec certificat ---
// options = { security: { certUrl, signUrl, algo } }
export function isQZLaunched(options) {
  // 1) Script non chargé
  if (!window.qz || !qz.websocket) {
    console.warn('[QZ] script qz-tray non chargé');
    return Promise.resolve(false);
  }

  // 2) Init sécurité (certificat + signature), idempotent
  if (options && options.security) {
    try {
      initSecurity(options.security);
    } catch (e) {
      console.error('[QZ] initSecurity failed', e);
    }
  }

  // 3) Déjà connecté → OK
  if (qz.websocket.isActive && qz.websocket.isActive()) {
    console.log('[QZ] isQZLaunched → déjà connecté');
    return Promise.resolve(true);
  }

  // 4) Sinon on passe par ensureConnected (même logique que printPayload)
  return ensureConnected()
    .then(function () {
      console.log('[QZ] isQZLaunched → connexion OK');
      return true;
    })
    .catch(function (e) {
      console.warn('[QZ] isQZLaunched → socket impossible à ouvrir', e);
      return false;
    });
}
